import { faCircleXmark } from '@fortawesome/free-solid-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Fuse from 'fuse.js'
import React, { ChangeEvent, createRef, useEffect, useState } from 'react'

import { useAppDispatch, useAppSelector } from '../../hooks'
import { MinimalTrackInfo } from '../../types'
import { initTrackIndex } from '../../actions/load-match'
import { selectCurrentUsername, selectTrackIndex } from '../../selectors/session-selectors'
import { getTrackArtistAndTitle } from '../../util/track-utils'
import { TextInputWithLabel } from './TextInputWithLabel'
import { selectAllMatchStatuses } from '../../selectors/match-selectors'
import { Link } from 'react-router-dom'
import { BLASTER_URL_PREFIX } from '../../constants/constants'
import { difficultyString, getTrackPoints } from '../../util/score-utils'
import Util from '../../util/util'

type PlaylistTrack = MinimalTrackInfo & {
  slug: string
  otherOwner: string
  duration: number
  wordCount: number
}

const fuseOptions = {
  keys: ['title', 'artist'],
  includeScore: true,
  includeMatches: true,
}

const TrackSearch = () => {
  const dispatch = useAppDispatch()
  const matchesByUser = useAppSelector(selectAllMatchStatuses)
  const trackIndex = useAppSelector(selectTrackIndex)
  const username = useAppSelector(selectCurrentUsername)
  const [fuse, setFuse] = React.useState<Fuse<PlaylistTrack>>(new Fuse([], fuseOptions))
  // const fuse = new Fuse(workingTracks, options)
  const [searchQuery, setSearchQuery] = useState('')
  const [trackForPopup, setTrackForPopup] = useState('')

  useEffect(() => {
    const currTracks = Object.keys(trackIndex).map((trackSlug) => {
      const trackInfo = trackIndex[trackSlug]
      const { owner = '', duration = 0, wordCount = 0 } = trackInfo || {}
      const otherOwner = username === owner ? '' : owner
      const { artist, title } = getTrackArtistAndTitle(trackInfo)
      return {
        slug: trackSlug,
        artist,
        title,
        duration,
        wordCount,
        otherOwner,
      }
    })
    setFuse(new Fuse(currTracks, fuseOptions))
  }, [trackIndex, username])
  const displayedTracks = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return []
    }

    const searchResults = fuse.search(searchQuery)
    return searchResults.map((result) => result.item)
  }, [searchQuery, fuse])

  const addTrack = ({ slug, artist, title, otherOwner, wordCount, duration }: PlaylistTrack) => {
    const showPopup = () => {
      setTrackForPopup(trackForPopup === slug ? '' : slug)
    }
    const getPopup = () => {
      if (trackForPopup !== slug) {
        return null
      }
      const matches = trackIndex[trackForPopup].matches
      return (
        <div className="searchPopup">
          {Object.keys(matches).map((owner) => {
            return (
              <div key={owner} className="matchOwner">
                <div>{owner}</div>
                {Object.keys(matches[owner]).map((matchSlug) => {
                  return (
                    <div className="match" key={`${owner}-${matchSlug}`}>
                      <div>{matchesByUser[owner][matchSlug].info.title}</div>
                      {matches[owner][matchSlug].map((playlistSlug) => {
                        return (
                          <div className="playlist">
                            <Link
                              to={`/${BLASTER_URL_PREFIX}/${owner}/${matchSlug}/${playlistSlug}/${slug}`}
                            >
                              {matchesByUser[owner][matchSlug].info.playlists[playlistSlug].title}
                            </Link>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      )
    }
    const durationClockTime = Util.secondsToClock(duration, false, false)
    const durationStr = duration > 0 ? durationClockTime : '-'
    const divider = <span>{' / '}</span>
    const { wordDensity } = getTrackPoints(wordCount, duration)
    return (
      <div key={slug} className="chatSongWrapper" onClick={showPopup}>
        <div className="searchResultItem">
          <div className="sidebarTrackTitle">
            <div>{title}</div>
            <div className="artist">{artist}</div>
          </div>
          <div className="sidebarTrackTitle right">
            <div className="songdetails">
              <span>{durationStr}</span>
              {divider}
              <span>{wordCount}</span>
              {divider}
              <span>{difficultyString(wordDensity)}</span>
            </div>
            {otherOwner && <div className="otherOwner">{otherOwner}</div>}
          </div>
        </div>
        {getPopup()}
      </div>
    )
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!Object.keys(trackIndex).length) {
      dispatch(initTrackIndex())
    } else {
      setSearchQuery(event.target.value)
    }
  }
  const onClear = () => {
    setSearchQuery('')
    if (searchBoxRef.current) {
      searchBoxRef.current.value = ''
    }
  }
  const searchBoxRef = createRef<HTMLInputElement>()
  return (
    <div className="search">
      <div className="searchBar">
        <TextInputWithLabel
          id="searchBar1"
          label=""
          placeholder="Search tracks..."
          value={searchQuery}
          onChange={onChange}
          onRef={searchBoxRef}
        />
        <div onClick={onClear}>
          <FontAwesomeIcon size="sm" icon={faCircleXmark as IconProp} title="Clear" />
        </div>
      </div>
      <div className="searchResults">{displayedTracks.map(addTrack)}</div>
    </div>
  )
}

export default TrackSearch
